// Section 24.4 and 24.14: report entries become prose here, outside the phases. The sim returns
// a template id and parameters; the UI's text table (ui/text/en.ts) holds the strings, so the
// sim never formats user-facing text and other languages stay possible.
import { registry } from '../content';
import type { GameState, ReportEntry } from '../core/types';
import type { CrimeKind, JusticeResponse } from '../core/types';
// Sections 14 and 19 (stream E2).
import { config } from '../config';
import type { ArrivalDecision, EmigrationReason, EventId, ImprovementId } from '../core/types';

/** Section 19 events that give a one-season warning (their `foreshadow` is set). */
export type ForeshadowEventId =
  'poor-harvest' | 'drought' | 'harsh-winter' | 'fever-outbreak' | 'raid';

/**
 * Section 19 choice ids per event, mirrored from the content files so every choice label and
 * outcome has text (tests/unit/events-text.test.ts keeps the two in sync). Stream E2.
 */
export interface EventChoiceIds {
  'poor-harvest': never;
  blight: 'burn-fields' | 'harvest-anyway';
  drought: 'dig-deeper' | 'ration';
  'harsh-winter': never;
  'early-frost': never;
  fire: never;
  'fever-outbreak': 'quarantine' | 'carry-on';
  plague: 'quarantine' | 'carry-on' | 'close-gates';
  'wild-animals': 'hunt-it' | 'avoid';
  raid: 'fight' | 'pay' | 'hide';
  refugees: 'admit-all' | 'admit-some' | 'guests' | 'turn-away';
  travellers: 'admit-all' | 'admit-some' | 'guests' | 'turn-away';
  'trade-caravan': 'trade-first' | 'trade-second' | 'decline';
  'good-harvest': never;
  'mild-winter': never;
  discovery: 'develop' | 'ignore';
  'improvement-proposal': 'commit' | 'decline';
  dispute: 'side-a' | 'side-b' | 'mediate';
  'elders-request': 'grant' | 'refuse';
  'birth-complication': 'assign-healer' | 'do-nothing';
  stranger: 'admit' | 'refuse';
  rumour: 'address' | 'let-go';
}

type Find = 'herb-patch' | 'fish-run' | 'clay-bed';
type ElderRequest = 'stop-working' | 'teach' | 'leave';

type ChoiceTemplate = {
  [E in keyof EventChoiceIds]: `choice.${E}.${EventChoiceIds[E]}`;
}[keyof EventChoiceIds];
type ChoseTemplate =
  | { [E in keyof EventChoiceIds]: `chose.${E}.${EventChoiceIds[E]}` }[keyof EventChoiceIds]
  | 'chose.drought.dig-deeper.none'
  | 'chose.wild-animals.hunt-it.none'
  | 'chose.stranger.admit.healer'
  | 'chose.stranger.admit.thief'
  | `chose.discovery.develop.${Find | 'none'}`
  | `chose.improvement-proposal.commit.${ImprovementId | 'none'}`
  | `chose.elders-request.grant.${ElderRequest}`
  | 'chose.birth-complication.assign-healer.none';

/** One-line consequence previews under each choice (Section 22 event prompt). */
export type PreviewKey =
  | 'arrival-admit'
  | 'arrival-admit-some'
  | 'arrival-guests'
  | 'arrival-turn-away'
  | 'arrival-turn-away-starving'
  | 'blight-burn'
  | 'blight-harvest'
  | 'drought-dig'
  | 'drought-ration'
  | 'quarantine'
  | 'carry-on'
  | 'close-gates'
  | 'hunt'
  | 'avoid'
  | 'raid-fight'
  | 'raid-pay'
  | 'raid-hide'
  | 'stranger-admit'
  | 'trade'
  | `discovery-${Find}`
  | 'improvement-commit'
  | 'improvement-no-worker'
  | 'dispute-side'
  | 'dispute-mediate'
  | `elder-grant-${ElderRequest}`
  | 'elder-refuse'
  | 'birth-healer'
  | 'birth-no-healer'
  | 'birth-alone'
  | 'rumour-address'
  | 'rumour-let-go';

/** Sections 14 and 19 template ids (stream E2): events, warnings, choices, the gate, departures. */
export type EventTemplateId =
  | `event.${EventId}`
  | 'event.blight.returns'
  | `event.refugees.${'famine' | 'raiders'}`
  | `event.discovery.${Find}`
  | `event.improvement-proposal.${ImprovementId}`
  | `event.elders-request.${ElderRequest}`
  | `event.wild-animals.${'hurt' | 'child' | 'hurt-child'}`
  | 'event.gate.followup'
  | `foreshadow.${ForeshadowEventId}`
  | ChoiceTemplate
  | ChoseTemplate
  | `preview.${PreviewKey}`
  | `arrival.${ArrivalDecision}`
  | `departure.${EmigrationReason}`
  | `departure-planned.${EmigrationReason}`
  | `improvement.${ImprovementId}`;

const EVENT_VARIANTS: ReadonlySet<string> = new Set([
  'blight.returns',
  'refugees.famine',
  'refugees.raiders',
  'discovery.herb-patch',
  'discovery.fish-run',
  'discovery.clay-bed',
  'improvement-proposal.crop-rotation',
  'improvement-proposal.better-kiln',
  'improvement-proposal.granary-lining',
  'elders-request.stop-working',
  'elders-request.teach',
  'elders-request.leave',
  'wild-animals.hurt',
  'wild-animals.child',
  'wild-animals.hurt-child',
]);

const CHOSE_VARIANTS: ReadonlySet<string> = new Set([
  'drought.dig-deeper.none',
  'wild-animals.hunt-it.none',
  'stranger.admit.healer',
  'stranger.admit.thief',
  'discovery.develop.herb-patch',
  'discovery.develop.fish-run',
  'discovery.develop.clay-bed',
  'discovery.develop.none',
  'improvement-proposal.commit.crop-rotation',
  'improvement-proposal.commit.better-kiln',
  'improvement-proposal.commit.granary-lining',
  'improvement-proposal.commit.none',
  'elders-request.grant.stop-working',
  'elders-request.grant.teach',
  'elders-request.grant.leave',
  'birth-complication.assign-healer.none',
]);

/** Section 19: an event's headline template, with its variant when it has one. */
export function eventTemplate(
  eventId: EventId,
  params: Readonly<Record<string, string | number>>,
): NarrationTemplateId {
  const variant = params.variant;
  const key = typeof variant === 'string' ? `${eventId}.${variant}` : '';
  return (EVENT_VARIANTS.has(key) ? `event.${key}` : `event.${eventId}`) as NarrationTemplateId;
}

/** Section 19: the template of a choice's outcome line. */
export function choseTemplate(
  eventId: EventId,
  choiceId: string,
  params: Readonly<Record<string, string | number>>,
): NarrationTemplateId {
  const variant = params.variant;
  const key = typeof variant === 'string' ? `${eventId}.${choiceId}.${variant}` : '';
  return (
    CHOSE_VARIANTS.has(key) ? `chose.${key}` : `chose.${eventId}.${choiceId}`
  ) as NarrationTemplateId;
}

/** Plain params only (the seed an event carries for its randomness is not for the reader). */
function readable(
  params: Readonly<Record<string, string | number>>,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) if (k !== 'seed') out[k] = v;
  return out;
}

/** The season a future turn falls in, seen from a state between seasons. */
function seasonOfTurn(state: GameState, turn: number): string {
  const order = config.seasons.order;
  const now = state.currentReport === null ? state.turn + 1 : state.turn;
  const i = order.indexOf(state.season) + turn - now;
  return order[((i % order.length) + order.length) % order.length] ?? state.season;
}

/** Sections 14 and 19 entries (stream E2), or null for other kinds. */
function narrateE2(entry: ReportEntry, state: GameState): Narration | null {
  switch (entry.kind) {
    case 'event':
      return {
        template: eventTemplate(entry.eventId, entry.params),
        params: readable(entry.params),
      };
    case 'event-choice':
      // A decision at the gate is told by its `arrival` entry.
      if (entry.promptId.startsWith('gate:')) return null;
      return {
        template: choseTemplate(entry.eventId, entry.choiceId, entry.params ?? {}),
        params: readable(entry.params ?? {}),
      };
    case 'foreshadow':
      return { template: `foreshadow.${entry.eventId}` as NarrationTemplateId, params: {} };
    case 'arrival': {
      // A group at the gate is told by its event's headline; its decision gets a line.
      if (entry.decision === null) return null;
      const size = entry.personIds.length;
      const admitted = entry.personIds.filter((id) => state.people[id] !== undefined).length;
      return {
        template: `arrival.${entry.decision}`,
        params: { size, admitted, turnedAway: size - admitted },
      };
    }
    case 'departure':
      return {
        template: `departure.${entry.reason}`,
        params: {
          names: entry.personIds.map((id) => nameOf(state, id)).join(', '),
          count: entry.personIds.length,
        },
      };
    case 'departure-planned':
      return {
        template: `departure-planned.${entry.reason}`,
        params: { name: nameOf(state, entry.personId), season: seasonOfTurn(state, entry.atTurn) },
      };
    case 'improvement':
      return { template: `improvement.${entry.improvementId}`, params: {} };
    default:
      return null;
  }
}

export type NarrationTemplateId =
  | 'death'
  | 'death.illness'
  | 'birth'
  | 'production'
  | 'production.service'
  | 'planted'
  | 'seed.reserved'
  | 'seed.eaten'
  | 'seed.short'
  | 'shortage'
  | 'rationing-outcome'
  | 'rationing-outcome.by-hand'
  | 'spoilage'
  | 'warning.food-short'
  | 'warning.water-short'
  | 'warning.fuel-short'
  | 'warning.no-toolmaker'
  | 'warning.tools-short'
  | 'warning.seed-not-reserved'
  | 'warning.build-blocked'
  | 'warning.build-limit'
  | 'warning.upkeep-overdue'
  | 'warning.plot-lost'
  | 'warning.farmers-without-plots'
  | 'warning.overcrowded'
  | 'warning.sleeping-rough'
  | 'warning.job-vacant'
  | 'warning.crops-look-thin'
  | 'warning.well-low'
  | 'warning.low-trust'
  | 'illness'
  | 'recovery'
  | 'injury'
  | 'injury.assault'
  | 'injury.raid'
  | 'injury.event'
  | 'treatment'
  | 'treatment.no-medicine'
  | 'passed-over'
  | 'quarantine.held'
  | 'quarantine.lifted'
  | 'job-vacant'
  | 'came-of-age'
  | 'became-elder'
  | 'building-complete'
  | 'construction'
  | 'build-cancelled'
  | 'building-damaged'
  | 'building-repaired'
  | 'building-upkeep'
  | 'building-frozen'
  | 'building-thawed'
  | 'tools'
  | 'knowledge-lost'
  | 'stunted'
  | 'policy-changed'
  // Sections 11, 15 and 16 (Stream D): schooling, family, norms, trust, festivals, refusal.
  | 'warning.children-uncovered'
  | 'miscarriage'
  | 'pregnancy'
  | 'pairing'
  | 'pairing.apart'
  | 'moved-in'
  | 'skill'
  | 'literate'
  | 'school'
  | 'school.waiting'
  | 'norm.established'
  | 'norm.lost'
  | 'norm.reinforced'
  | 'trust.up'
  | 'trust.down'
  | 'festival'
  | 'festival-skipped.no-hall'
  | 'festival-skipped.short-food'
  | 'festival-skipped.short-wood'
  | 'refusal'
  // Section 13 (stream M4a): crimes, unexplained losses and justice.
  | `crime.${CrimeKind}`
  | `crime.${CrimeKind}.unexplained`
  | `justice.${JusticeResponse}`
  // Sections 14 and 19 (stream E2).
  | EventTemplateId
  | 'event'
  | 'steady.pass'
  | 'steady.fail'
  | 'outcome.won'
  | 'outcome.lost'
  | 'generic';

export interface Narration {
  template: NarrationTemplateId;
  params: Record<string, string | number>;
}

function nameOf(state: GameState, id: string): string {
  const key = id as keyof GameState['people'];
  return state.people[key]?.name ?? state.formerPeople[key]?.name ?? id;
}

const round1 = (x: number): number => Math.round(x * 10) / 10;

/** Structured narration for one entry, or null for entries that are not narrated. */
export function narrate(entry: ReportEntry, state: GameState): Narration | null {
  switch (entry.kind) {
    case 'death': {
      const factors = entry.factors.map((f) => f.note ?? f.source).join('; ');
      const params = {
        name: entry.name,
        age: Math.floor(entry.ageSeasons / 4),
        cause: entry.cause,
        factors,
      };
      return entry.illnessId === undefined
        ? { template: 'death', params }
        : { template: 'death.illness', params: { ...params, illness: entry.illnessId } };
    }
    case 'birth':
      return {
        template: 'birth',
        params: { name: entry.name, mother: nameOf(state, entry.motherId) },
      };
    case 'production':
      return {
        template:
          entry.resource === 'buildPoints' ||
          entry.resource === 'healingSlots' ||
          entry.resource === 'teachingSeats' ||
          entry.resource === 'careSlots' ||
          entry.resource === 'safetyPoints'
            ? 'production.service'
            : 'production',
        params: {
          job: entry.jobId,
          resource: entry.resource,
          amount: round1(entry.amount),
          workers: entry.workers,
        },
      };
    case 'planted':
      return { template: 'planted', params: { plots: entry.plots, seed: round1(entry.seedUsed) } };
    case 'seed':
      return { template: `seed.${entry.action}`, params: { amount: round1(entry.amount) } };
    case 'shortage':
      return {
        template: 'shortage',
        params: {
          resource: entry.resource,
          covered: entry.covered,
          population: entry.population,
          shortfall: round1(entry.shortfall),
        },
      };
    case 'rationing-outcome':
      // Section 17: who went short and whether the player chose by hand.
      return {
        template: entry.byHand ? 'rationing-outcome.by-hand' : 'rationing-outcome',
        params: {
          resource: entry.resource,
          // Group ids, comma-separated; the UI's text table renders their labels.
          groups: (entry.shortGroups.some((g) => g !== 'everyone-else')
            ? entry.shortGroups.filter((g) => g !== 'everyone-else')
            : entry.shortGroups
          ).join(','),
          none: entry.pinnedNone.map((id) => nameOf(state, id)).join(', '),
          deviated: entry.deviated.length,
          shared: round1(entry.shared),
        },
      };
    case 'spoilage':
      return {
        template: 'spoilage',
        params: { resource: entry.resource, amount: round1(entry.amount) },
      };
    case 'warning':
      return {
        template: `warning.${entry.code}`,
        params: {
          resource: entry.resource ?? '',
          amount: round1(entry.amount ?? 0),
          job: entry.jobId ?? '',
          seasons: entry.seasons ?? 0,
          name: entry.personId === undefined ? '' : nameOf(state, entry.personId),
          building: entry.buildingType ?? '',
        },
      };
    case 'illness':
      return {
        template: 'illness',
        params: { name: nameOf(state, entry.personId), illness: entry.illnessId },
      };
    case 'recovery':
      return {
        template: 'recovery',
        params: { name: nameOf(state, entry.personId), illness: entry.illnessId },
      };
    case 'injury':
      // M6 (Stream C/E1 request): an assault or an event is not an injury "at work".
      return {
        template: entry.source.startsWith('crime:')
          ? 'injury.assault'
          : entry.source === 'event:raid'
            ? 'injury.raid'
            : entry.source.startsWith('event:')
              ? 'injury.event'
              : 'injury',
        params: { name: nameOf(state, entry.personId), job: entry.jobId ?? '' },
      };
    case 'treatment':
      // Section 12 (stream M3b): who was treated, by whom, with or without medicine.
      return {
        template: entry.withMedicine ? 'treatment' : 'treatment.no-medicine',
        params: {
          name: nameOf(state, entry.patientId),
          healer: entry.healerId === null ? '' : nameOf(state, entry.healerId),
        },
      };
    case 'passed-over':
      return { template: 'passed-over', params: { name: nameOf(state, entry.personId) } };
    case 'quarantine':
      return {
        template: entry.change === 'held' ? 'quarantine.held' : 'quarantine.lifted',
        params: { people: entry.people, food: round1(entry.food) },
      };
    case 'job-vacant':
      return {
        template: 'job-vacant',
        params: { name: nameOf(state, entry.personId), job: entry.jobId },
      };
    case 'came-of-age':
    case 'became-elder':
    case 'stunted':
      return { template: entry.kind, params: { name: nameOf(state, entry.personId) } };
    case 'building-complete':
      return { template: 'building-complete', params: { building: entry.buildingType } };
    case 'construction':
      return {
        template: 'construction',
        params: {
          building: entry.buildingType,
          points: round1(entry.points),
          remaining: round1(entry.remaining),
        },
      };
    // Section 9 and 10 (stream M3a). Materials are wood and stone for every building.
    case 'build-cancelled':
      return {
        template: 'build-cancelled',
        params: {
          building: entry.buildingType,
          wood: round1(entry.refunded.wood ?? 0),
          stone: round1(entry.refunded.stone ?? 0),
        },
      };
    case 'building-damaged':
      return {
        template: 'building-damaged',
        params: {
          building: state.buildings[entry.buildingId]?.type ?? '',
          amount: round1(entry.amount),
        },
      };
    case 'building-repaired':
      return { template: 'building-repaired', params: { building: entry.buildingType } };
    case 'building-upkeep':
      return {
        template: 'building-upkeep',
        params: {
          building: entry.buildingType,
          wood: round1(entry.materials.wood ?? 0),
          stone: round1(entry.materials.stone ?? 0),
        },
      };
    case 'building-frozen':
      return {
        template: entry.frozen ? 'building-frozen' : 'building-thawed',
        params: { building: entry.buildingType },
      };
    case 'tools':
      return {
        template: 'tools',
        params: {
          made: round1(entry.made),
          repaired: round1(entry.repaired),
          worn: round1(entry.worn),
        },
      };
    case 'knowledge-lost':
      return {
        template: 'knowledge-lost',
        params: { name: nameOf(state, entry.personId), skill: entry.skill },
      };
    case 'crime':
      // Section 13 (stream M4a): detected crimes name the offender; undetected ones are losses.
      return {
        template: entry.detected ? `crime.${entry.crime}` : `crime.${entry.crime}.unexplained`,
        params: {
          offender: entry.offenderId === null ? '' : nameOf(state, entry.offenderId),
          victim: entry.victimId === undefined ? '' : nameOf(state, entry.victimId),
          amount: round1(entry.amount ?? 0),
          building:
            entry.buildingId === undefined ? '' : (state.buildings[entry.buildingId]?.type ?? ''),
        },
      };
    case 'justice':
      return {
        template: `justice.${entry.response}`,
        params: { offender: nameOf(state, entry.offenderId) },
      };
    case 'policy-changed':
      return {
        template: 'policy-changed',
        params: { policy: entry.policyId, trust: entry.trustCost },
      };
    // Sections 11, 15 and 16 (Stream D).
    case 'miscarriage':
      return { template: 'miscarriage', params: { name: nameOf(state, entry.personId) } };
    case 'pregnancy':
      return {
        template: 'pregnancy',
        params: {
          name: nameOf(state, entry.personId),
          father: entry.fatherId === null ? '' : nameOf(state, entry.fatherId),
        },
      };
    case 'pairing':
    case 'moved-in':
      return {
        template: entry.kind === 'pairing' && entry.apart === true ? 'pairing.apart' : entry.kind,
        params: { a: nameOf(state, entry.personIds[0]), b: nameOf(state, entry.personIds[1]) },
      };
    case 'skill':
      return {
        template: 'skill',
        params: { name: nameOf(state, entry.personId), job: entry.skill, level: entry.level },
      };
    case 'literate':
      return { template: 'literate', params: { name: nameOf(state, entry.personId) } };
    case 'school':
      return {
        template: entry.waiting > 0 ? 'school.waiting' : 'school',
        params: {
          students: entry.students,
          teachers: entry.teachers,
          waiting: entry.waiting,
          subjects: entry.subjects.join(','),
        },
      };
    case 'norm':
      return {
        template: `norm.${entry.change}`,
        params: { norm: registry.norms[entry.normId].label },
      };
    case 'trust':
      return {
        template: entry.delta >= 0 ? 'trust.up' : 'trust.down',
        params: { reason: entry.cause, delta: round1(Math.abs(entry.delta)), people: entry.people },
      };
    case 'festival':
      return {
        template: 'festival',
        params: { food: round1(entry.foodCost), wood: round1(entry.woodCost) },
      };
    case 'festival-skipped':
      return { template: `festival-skipped.${entry.reason}`, params: {} };
    case 'refusal':
      return { template: 'refusal', params: { households: entry.householdIds.length } };
    // Sections 14 and 19 (stream E2).
    case 'event':
    case 'event-choice':
    case 'foreshadow':
    case 'arrival':
    case 'departure':
    case 'departure-planned':
    case 'improvement':
      return narrateE2(entry, state);
    case 'steady': {
      const failed = Object.entries(entry.result.conditions)
        .filter(([, c]) => !c.pass)
        .map(([k]) => k);
      return entry.result.allPass
        ? {
            template: 'steady.pass',
            params: { counter: entry.result.counter, target: entry.result.target },
          }
        : {
            template: 'steady.fail',
            params: { failed: failed.join(', '), target: entry.result.target },
          };
    }
    case 'outcome':
      return {
        template: entry.outcome === 'won' ? 'outcome.won' : 'outcome.lost',
        params: { reason: entry.reason },
      };
    default:
      return null;
  }
}

/** Narrate a whole report, dropping entries that are not narrated. */
export function narrateAll(entries: readonly ReportEntry[], state: GameState): Narration[] {
  const out: Narration[] = [];
  for (const e of entries) {
    const n = narrate(e, state);
    if (n !== null) out.push(n);
  }
  return out;
}
